class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');
    this.voiceButton = this.querySelector(".search-voice-button");
    this.voiceRecognition = null;
    this.isVoiceListening = false;

    this.initializeVoiceSearch();

    if (this.input?.form) {
      this.input.form.addEventListener("reset", this.onFormReset.bind(this));
      this.input.addEventListener(
        "input",
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this),
      );
    }
  }

  initializeVoiceSearch() {
    // Only enable the microphone when the browser exposes the Web Speech API and the page is served over HTTPS.
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    const isSupported = Boolean(SpeechRecognition) && window.isSecureContext;

    if (!this.voiceButton || !isSupported) {
      this.voiceButton?.setAttribute("hidden", true);
      return;
    }

    this.voiceButton.removeAttribute("hidden");
    this.voiceButton.addEventListener(
      "click",
      this.handleVoiceButtonClick.bind(this),
    );
  }

  handleVoiceButtonClick(event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.isVoiceListening) {
      this.stopVoiceRecognition();
      return;
    }

    this.startVoiceRecognition();
  }

  getVoiceLanguage() {
    const languageFromDocument =
      document.documentElement.lang ||
      this.voiceButton?.dataset.voiceLanguage ||
      "en-US";
    const normalizedLanguage = String(languageFromDocument).toLowerCase();

    if (normalizedLanguage.startsWith("ar")) return "ar-SA";
    if (normalizedLanguage.startsWith("he")) return "he-IL";
    if (normalizedLanguage.startsWith("en")) return "en-US";

    return this.voiceButton?.dataset.voiceLanguage || "en-US";
  }

  startVoiceRecognition() {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition || !this.input) {
      return;
    }

    if (this.voiceRecognition) {
      this.voiceRecognition.stop();
    }

    this.voiceRecognition = new SpeechRecognition();
    this.voiceRecognition.lang = this.getVoiceLanguage();
    this.voiceRecognition.continuous = false;
    this.voiceRecognition.interimResults = false;
    this.voiceRecognition.maxAlternatives = 1;

    this.voiceRecognition.onstart = () => {
      this.isVoiceListening = true;
      this.voiceButton?.classList.add("search-voice-button--listening");
      this.voiceButton?.setAttribute("aria-pressed", "true");
      this.voiceButton?.setAttribute("aria-label", "Stop voice search");
      this.input?.focus();
    };

    this.voiceRecognition.onerror = () => {
      this.resetVoiceButtonState();
      this.input?.focus();
    };

    this.voiceRecognition.onend = () => {
      this.resetVoiceButtonState();
    };

    this.voiceRecognition.onresult = (event) => {
      const transcript = Array.from(event.results)
        .map((result) => result[0]?.transcript || "")
        .join(" ")
        .trim();

      if (!transcript) {
        return;
      }

      if (this.input) {
        this.input.value = transcript;
        this.input.dispatchEvent(new Event("input", { bubbles: true }));
        this.input.dispatchEvent(new Event("change", { bubbles: true }));
        this.input.focus();
      }

      if (!this.supportsPredictiveSearch()) {
        this.input?.form?.requestSubmit();
      }
    };

    this.voiceRecognition.start();
  }

  stopVoiceRecognition() {
    this.voiceRecognition?.stop();
    this.resetVoiceButtonState();
  }

  supportsPredictiveSearch() {
    return (
      this.tagName.toLowerCase() === "predictive-search" ||
      this.closest("predictive-search") !== null
    );
  }

  resetVoiceButtonState() {
    this.isVoiceListening = false;
    this.voiceButton?.classList.remove("search-voice-button--listening");
    this.voiceButton?.setAttribute("aria-pressed", "false");
    this.voiceButton?.setAttribute("aria-label", "Voice search");
  }

  toggleResetButton() {
    if (!this.resetButton) {
      return;
    }

    const hasValue = this.input?.value?.length > 0;
    const resetIsHidden = this.resetButton.classList.contains("hidden");

    if (hasValue && resetIsHidden) {
      this.resetButton.classList.remove("hidden");
    } else if (!hasValue && !resetIsHidden) {
      this.resetButton.classList.add("hidden");
    }
  }

  onChange() {
    this.toggleResetButton();
  }

  shouldResetForm() {
    return !document.querySelector('[aria-selected="true"] a');
  }

  onFormReset(event) {
    event.preventDefault();

    if (!this.shouldResetForm()) {
      return;
    }

    if (this.input) {
      this.input.value = "";
      this.input.focus();
      this.input.dispatchEvent(new Event("input", { bubbles: true }));
      this.input.dispatchEvent(new Event("change", { bubbles: true }));
    }

    this.toggleResetButton();
  }
}

customElements.define("search-form", SearchForm);
